// V1 source: customer/midas/components/MidasArtifactDiagramViewer.tsx
// Strategy: logic-copied
// Verified no-MUI: yes
//
// V2 diagram viewer — Tier A behavioral parity with V1. Logic copied verbatim:
//   - Single rendering mode: CSS object-fit:contain at all times. scale=1
//     IS the fit-to-view state.
//   - Mouse wheel zoom-toward-cursor (HARD GUARD G1 — formula at lines
//     ~210-244 must NOT be touched, character-for-character copy from V1).
//   - Click-drag pan via mousedown/mousemove/mouseup window listeners.
//   - SVG hotspot overlay positioned to match where object-fit:contain
//     places the image, viewBox 0 0 100 100 with preserveAspectRatio=none.
//   - getContainedRect helper, ResizeObserver, image-load → updateContainedRect.
//   - Step navigation Prev/Next cycling highlightedHotspots.
//   - Tooltip (DiagramInfoCardV2) follows cursor; selectedId backdrop click clears.
//     OVERLAY-CLICK-HIGHLIGHT-1 (2026-10-04): selectedId is now DRAWN — the
//     selected box is highlighted (DiagramHotspotRectV2 `isSelected`). Click
//     on box / pill / leader toggles it, backdrop click clears it, page change
//     resets it. Hover is unchanged (info card only). A drag-pan no longer
//     counts as a click (PAN_CLICK_SLOP_PX), so panning keeps the selection.
//     PARTS-SERVING-1 (2026-10-06): a click that selects a hotspot carrying
//     parts / details / links ALSO pins its info card at the click point
//     (DiagramPinnedInfoCardV2: clickable sheet links, internal scroll). The
//     pin lives exactly as long as that selection; hotspots without those
//     fields behave exactly as before (click = highlight only).
//   - Reset view returns to scale=1, pan=(0,0).
//   - Overlay toggle (show/hide hotspots) preserves zoom + pan.
//   - Fetch image + hotspots with Authorization Bearer + X-UI-Language.
//
// Visual layer rewritten in Tailwind. Lucide-react icons replace MUI icons.
// shadcn Tooltip primitive for icon button labels.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Maximize2,
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeOff,
} from "lucide-react";

import { ArtifactTab } from "../../midasPageModels";
// DIAG-OVERLAY-B: shared flash duration + the reduced-motion fork. Lives in
// the neutral `customer/midas/utils/` layer (same as diagramColorMap below) so
// this v2 file never has to import from v3.
import {
  COMP_FLASH_DURATION_MS,
  prefersReducedMotion,
} from "../../utils/compRefFlash";
import {
  DiagramHotspot,
  DiagramLabelPill,
  DiagramOverlayArtifactData,
  DiagramWiringConnection,
  DiagramCrossReference,
  DiagramWireLabel,
} from "../../../../shared/chatBot/models/chatBotModels";
// OVERLAY-LABEL-PLACEMENT-1: served label anchors (validation + gutter math).
import {
  computeLabelGutterPct,
  containWithLeftGutter,
  parseLabelPill,
  planHotspotLabel,
  resolveImageAspect,
} from "../../utils/diagramLabelAnchor";
import useTranslation from "../../../../shared/hooks/useTranslation";
import accountContext from "../../../../account/accountContext";
import DiagramHotspotRectV2 from "./DiagramHotspotRectV2";
import DiagramInfoCardV2 from "./DiagramInfoCardV2";
// PARTS-SERVING-1: pinned (clickable) popup + the "has part details" test.
import DiagramPinnedInfoCardV2 from "./DiagramPinnedInfoCardV2";
import {
  hasHotspotBodyExtras,
  parseHotspotExtras,
} from "../../utils/diagramHotspotParts";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";

// ===================================================================
// Props
// ===================================================================

interface Props {
  tab: ArtifactTab;
  /**
   * MIDAS-V3-P3 (2026-05-18): externally-controlled overlay visibility.
   *
   * When the v3 dock wraps this viewer it owns the eye-toggle in the v3
   * footer (via midasV3Slice.overlayVisibility), so the caller can force
   * overlays on/off without the viewer's internal toolbar.
   *
   * When undefined (the v2 caller — `MidasArtifactPanelV2`), this prop is
   * ignored and the viewer uses its own local `showOverlays` state exactly
   * as it always has — i.e. v2 behaviour is byte-identical. Default `true`
   * preserves visible-on-mount behaviour for any caller that opts into the
   * prop but doesn't supply a value (e.g. a future ref-style consumer).
   */
  overlaysVisible?: boolean;
  /**
   * R5 sub-task 2a (2026-05-19): hide the v2 viewer's internal bottom-toolbar
   * eye toggle when wrapped by v3. v3 owns the eye toggle in the header
   * (R3 sub-task 1) — having it ALSO appear inside the diagram body bottom
   * toolbar is redundant and confusing (two eyes ~5cm apart).
   *
   * When undefined or false (v2 caller — `MidasArtifactPanelV2`), the v2
   * eye toggle renders exactly as before — v2 behaviour is byte-identical.
   * v3 caller (`MidasArtifactPaneV3 → ArtifactContentV3`) passes `true`.
   *
   * Same byte-additive pattern as `overlaysVisible` above — introduced solely
   * for v3 chrome integration.
   *
   * (Amended 2026-08-21, DIAG-OVERLAY-B: this comment previously claimed to be
   * "the second and last v2 viewer prop". The comp-ref flash props below are a
   * third addition, on the same byte-additive terms — every one of them is
   * optional and `undefined` reproduces pre-existing v2 behaviour exactly.)
   */
  hideInternalEyeToggle?: boolean;
  /**
   * DIAG-OVERLAY-B: hotspot `component_id` to flash TRANSIENTLY on this sheet.
   *
   * "Flash" = a ~1.6s outline pulse around ALL matching hotspot instances
   * (a component id is a MULTISET on a sheet — e.g. `XB1:00` appears 4x on KMX
   * page 1043 — and every instance flashes simultaneously, N generic). It is
   * NOT a selection and NOT a filter: nothing persists, `selectedId` is not
   * touched, and no other hotspot is hidden or dimmed by it.
   *
   * undefined (the v2 caller, and the v3 caller with no pending flash) = no
   * flash, byte-identical to the pre-DIAG-OVERLAY-B viewer.
   */
  flashComponentId?: string;
  /**
   * Monotonic arm counter from the dock. REQUIRED for the flash to fire.
   *
   * Without it, clicking the same chip twice would pass an unchanged
   * `flashComponentId` and React would see no prop change — so the second
   * click would silently do nothing. The nonce makes every arm distinct.
   */
  flashNonce?: number;
  /**
   * Fired once, when this viewer has actually STARTED a flash (i.e. hotspots
   * were loaded and the latch was taken). The dock consumes the pending entry
   * here; the flash's remaining lifetime is owned locally, so the props going
   * undefined immediately afterwards is expected and harmless.
   */
  onFlashArmed?: () => void;
  /**
   * PARTS-SERVING-1: open a sheet of the SAME document (a hotspot `links[]`
   * `page_id`) in the overlay viewer. The caller owns navigation (the dock
   * tabs — `openDiagramSheet`), so the new sheet loads through this viewer's
   * existing `/v1/bot/diagram/{page}/…` fetch and entitlement.
   *
   * undefined = the pinned card shows its links as plain text. Same
   * byte-additive terms as the props above.
   */
  onOpenSheet?: (pageId: number) => void;
}

// ===================================================================
// Helpers — verbatim from V1
// ===================================================================

/**
 * Calculate where object-fit:contain places the image within its container.
 *
 * OVERLAY-LABEL-PLACEMENT-1: `gutterPct` > 0 (only when the page carries
 * served label anchors left of the image) fits image + left gutter as one
 * block; the img gets a matching padding-left so the pixels line up. With the
 * default 0 the original V1 formula below runs unchanged.
 */
const getContainedRect = (
  container: HTMLElement | null,
  img: HTMLImageElement | null,
  gutterPct: number = 0
): { left: number; top: number; width: number; height: number } => {
  if (!container || !img) return { left: 0, top: 0, width: 0, height: 0 };
  const cw = container.clientWidth;
  const ch = container.clientHeight;
  const iw = img.naturalWidth;
  const ih = img.naturalHeight;
  if (!iw || !ih) return { left: 0, top: 0, width: cw, height: ch };
  if (gutterPct > 0) return containWithLeftGutter(cw, ch, iw, ih, gutterPct);

  const s = Math.min(cw / iw, ch / ih);
  const renderedW = iw * s;
  const renderedH = ih * s;
  const left = (cw - renderedW) / 2;
  const top = (ch - renderedH) / 2;
  return { left, top, width: renderedW, height: renderedH };
};

/**
 * OVERLAY-CLICK-HIGHLIGHT-1: pointer travel (px) between mousedown and the
 * browser's trailing `click` above which the gesture was a PAN, not a click.
 * Browsers fire `click` after a drag whenever mousedown and mouseup share an
 * ancestor, so without this every pan cleared (backdrop) or toggled (box) the
 * now-visible selection. Below the slop a jittery click still counts.
 */
const PAN_CLICK_SLOP_PX = 4;

// ===================================================================
// MidasArtifactDiagramViewerV2
// ===================================================================

const MidasArtifactDiagramViewerV2: React.FC<Props> = ({
  tab,
  overlaysVisible,
  hideInternalEyeToggle,
  flashComponentId,
  flashNonce,
  onFlashArmed,
  onOpenSheet,
}) => {
  const { translate } = useTranslation("chatBot");
  const viewportRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  const artifactData = tab.artifact.data as DiagramOverlayArtifactData | undefined;
  const page = artifactData?.page ?? tab.artifact.page ?? 0;

  const highlightedComponentIds = useMemo(
    () => new Set(artifactData?.components ?? []),
    [artifactData?.components]
  );

  // Data state
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [hotspots, setHotspots] = useState<DiagramHotspot[]>([]);
  // DIAG-OVERLAY-B: which page the CURRENT `hotspots` array belongs to.
  //
  // Needed because React state set inside an effect is not visible to the
  // other effects of the same commit: on a page change, `hotspots` still holds
  // the PREVIOUS sheet's data while every effect in that pass runs. Without
  // this guard the flash could arm against the old sheet's hotspots (the v3
  // dock remounts per tab via key={tab.id} so it can't hit that today, but the
  // v2 caller reuses one instance and a future v3 change could too). Comparing
  // page identity instead of array emptiness makes the arm correct regardless
  // of effect ordering.
  const [hotspotsPage, setHotspotsPage] = useState<number | null>(null);
  // OVERLAY-LABEL-PLACEMENT-1: validated top-level `label_pill`, or null.
  const [labelPill, setLabelPill] = useState<DiagramLabelPill | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Overlay visibility toggle. When the v3 caller passes `overlaysVisible`
  // it wins over local state (the v3 footer owns the eye-toggle there).
  const [localShowOverlays, setLocalShowOverlays] = useState(true);
  const showOverlays =
    overlaysVisible === undefined ? localShowOverlays : overlaysVisible;
  const setShowOverlays = setLocalShowOverlays;

  // Selection / step navigation
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [activeStepIdx, setActiveStepIdx] = useState(0);

  // Tooltip state
  const [tooltipHotspot, setTooltipHotspot] = useState<DiagramHotspot | null>(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  // PARTS-SERVING-1: pinned card = which hotspot + where it was clicked.
  // Shown only while `pinned.id === selectedId` (the effect below drops it
  // the moment the selection moves away). pointerRef = last pointer position
  // over a hotspot (hover tracking), read by the click handler.
  const [pinned, setPinned] = useState<{ id: string; x: number; y: number } | null>(null);
  const pointerRef = useRef({ x: 0, y: 0 });

  // Pan / zoom state — single mode, always active
  const [scale, setScale] = useState(1);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  // SVG overlay rect — recalculated on resize and image load
  const [containedRect, setContainedRect] = useState({ left: 0, top: 0, width: 0, height: 0 });

  // Refs to avoid stale closures in event handlers
  const scaleRef = useRef(1);
  const panXRef = useRef(0);
  const panYRef = useRef(0);
  const dragStartRef = useRef({ x: 0, y: 0, panX: 0, panY: 0 });
  // OVERLAY-CLICK-HIGHLIGHT-1: true once the current press moved beyond the
  // slop; reset on every mousedown. Read by the click handlers below.
  const pannedRef = useRef(false);

  // Page-level data (stored for Phase 3 usage)
  const [, setWiringConnections] = useState<DiagramWiringConnection[]>([]);
  const [, setCrossReferences] = useState<DiagramCrossReference[]>([]);
  const [, setWireLabels] = useState<DiagramWireLabel[]>([]);

  const baseUrl = (window as any).app?.config?.apiBaseUrl || "";

  // Keep refs in sync
  useEffect(() => { scaleRef.current = scale; }, [scale]);
  useEffect(() => { panXRef.current = panX; }, [panX]);
  useEffect(() => { panYRef.current = panY; }, [panY]);

  // Helper: check if a hotspot is highlighted
  const isHotspotHighlighted = useCallback(
    (h: DiagramHotspot) => {
      if (highlightedComponentIds.size === 0) return false;
      return h.component_id ? highlightedComponentIds.has(h.component_id) : false;
    },
    [highlightedComponentIds]
  );

  const highlightedHotspots = useMemo(
    () => hotspots.filter(isHotspotHighlighted),
    [hotspots, isHotspotHighlighted]
  );

  const hasAnyHighlighted = highlightedHotspots.length > 0;

  // ── DIAG-OVERLAY-B: transient component flash ────────────────────
  //
  // Latched local state, NOT a derived prop read. The dock consumes its
  // pending entry the instant we arm, so `flashComponentId` goes undefined
  // almost immediately — the latch is what keeps the flash alive for its full
  // duration. `nonce` is carried into the latch so re-arming the SAME
  // component produces a new object identity and therefore a fresh timer
  // (a plain string would compare equal and freeze the flash at the old
  // deadline).
  const [compFlash, setCompFlash] = useState<{
    id: string;
    nonce: number;
  } | null>(null);
  const armedFlashNonceRef = useRef<number | null>(null);

  // Reduced motion (spec §4.9): the flash is an SVG SMIL <animate>, which the
  // v3 stylesheet's `prefers-reduced-motion` override cannot neutralise — CSS
  // only reaches CSS animations. So the fork is decided here and passed down
  // as a mode. Read once per render (cheap; matchMedia is a live object but a
  // mid-flash preference change is not worth a listener).
  const flashMode: "pulse" | "static" = prefersReducedMotion()
    ? "static"
    : "pulse";

  // Page change (the v2 caller reuses one instance across tabs; the v3 caller
  // remounts via key={tab.id}) invalidates any latch — a flash armed for the
  // previous sheet must never bleed onto the next one.
  useEffect(() => {
    armedFlashNonceRef.current = null;
    setCompFlash(null);
  }, [page]);

  // Arm. Deliberately gated on THIS page's hotspots having landed, so a
  // freshly-revealed background tab flashes AFTER its fetch resolves rather
  // than against an empty (or previous-sheet) overlay — spec §3.6 "Timing".
  // A sheet whose hotspots never load simply never arms: no flash, no error,
  // and the pending survives until the next chip click replaces it.
  useEffect(() => {
    if (!flashComponentId || flashNonce === undefined) return;
    if (armedFlashNonceRef.current === flashNonce) return; // already handled
    if (hotspotsPage !== page || hotspots.length === 0) return; // wait for fetch
    armedFlashNonceRef.current = flashNonce;
    setCompFlash({ id: flashComponentId, nonce: flashNonce });
    if (onFlashArmed) onFlashArmed();
  }, [
    flashComponentId,
    flashNonce,
    hotspots,
    hotspotsPage,
    page,
    onFlashArmed,
  ]);

  // Expire. One timer per latch; the object identity in the dep array is what
  // restarts it on a re-arm.
  useEffect(() => {
    if (!compFlash) return;
    const timer = window.setTimeout(
      () => setCompFlash(null),
      COMP_FLASH_DURATION_MS
    );
    return () => window.clearTimeout(timer);
  }, [compFlash]);

  // Multiset match: EVERY hotspot carrying this component_id flashes at once.
  // Zero matches (shouldn't happen per contract) degrades to no flash.
  const isHotspotFlashing = useCallback(
    (h: DiagramHotspot) =>
      compFlash !== null && !!h.component_id && h.component_id === compFlash.id,
    [compFlash]
  );

  // ── OVERLAY-LABEL-PLACEMENT-1: served label anchors ──────────────
  //
  // Index-aligned with `hotspots`. No valid `label_pill` → null: every
  // hotspot keeps the original label rule (the pill size is what the anchors
  // were computed for). On a page without anchors (SBL500, every non-KMX
  // document) gutter = 0 and nothing below changes.
  //
  // OVERLAY-LABEL-HIDDEN-ONLY-1 (2026-10-04): with a valid pill, each hotspot
  // is planned by `planHotspotLabel` — anchored (valid anchor), none (no
  // anchor served = its box is reachable, no label), or legacy (anchor
  // present but malformed = original rule, so a hidden box keeps a label).
  const labelPlans = useMemo(
    () =>
      labelPill
        ? hotspots.map((h) => planHotspotLabel(labelPill, h.label_anchor))
        : null,
    [hotspots, labelPill]
  );
  const labelAnchors = useMemo(
    () => (labelPlans ? labelPlans.map((p) => p.anchor) : null),
    [labelPlans]
  );
  const hasLabelAnchors = !!labelAnchors && labelAnchors.some((a) => a !== null);
  const labelGutterPct = labelAnchors ? computeLabelGutterPct(labelAnchors) : 0;

  // ── Update contained rect (for SVG overlay positioning) ─────
  // Identity changes only when the gutter does (0 → N once per anchored
  // page), which re-subscribes the ResizeObserver and re-measures.
  const updateContainedRect = useCallback(() => {
    const rect = getContainedRect(viewportRef.current, imgRef.current, labelGutterPct);
    setContainedRect(rect);
  }, [labelGutterPct]);

  // ── Fetch image + hotspots ────────────────────────────────────────
  useEffect(() => {
    setLoading(true);
    setError(false);
    setBlobUrl(null);
    setHotspots([]);
    setHotspotsPage(null); // DIAG-OVERLAY-B: hotspots no longer describe any page
    setLabelPill(null);
    setSelectedId(null);
    setPinned(null); // PARTS-SERVING-1
    setActiveStepIdx(0);
    setTooltipHotspot(null);
    setWiringConnections([]);
    setCrossReferences([]);
    setWireLabels([]);
    setScale(1);
    setPanX(0);
    setPanY(0);
    scaleRef.current = 1;
    panXRef.current = 0;
    panYRef.current = 0;

    if (page < 1) {
      setError(true);
      setLoading(false);
      return;
    }

    let revoked = false;
    const token = accountContext.accessToken;
    const headers: Record<string, string> = {
      "X-UI-Language": localStorage.getItem("i18nextLng") || "sk",
    };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const imagePromise = fetch(`${baseUrl}/v1/bot/diagram/${page}/image`, { headers })
      .then((res) => {
        if (!res.ok) throw new Error(`Image fetch failed: ${res.status}`);
        return res.blob();
      });

    const hotspotsPromise = fetch(`${baseUrl}/v1/bot/diagram/${page}/hotspots`, { headers })
      .then((res) => {
        if (!res.ok) throw new Error(`Hotspots fetch failed: ${res.status}`);
        return res.json();
      });

    Promise.all([imagePromise, hotspotsPromise])
      .then(([blob, data]) => {
        if (revoked) return;
        const url = URL.createObjectURL(blob);
        setBlobUrl(url);
        setHotspots(data.hotspots || []);
        setHotspotsPage(page); // DIAG-OVERLAY-B: stamp the page these belong to
        setLabelPill(parseLabelPill(data.label_pill)); // OVERLAY-LABEL-PLACEMENT-1
        setWiringConnections(data.wiring_connections || []);
        setCrossReferences(data.cross_references || []);
        setWireLabels(data.wire_labels || []);
        setLoading(false);
      })
      .catch(() => {
        if (!revoked) {
          setError(true);
          setLoading(false);
        }
      });

    return () => { revoked = true; };
  }, [page, baseUrl]);

  // Clean up blob URL on unmount
  useEffect(() => {
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [blobUrl]);

  // ── Mouse wheel zoom (toward cursor) ─────────────────────────────
  // Uses transformOrigin: 50% 50% with translate(panX, panY) scale(scale).
  // For a wrapper of size (W, H) with origin at center, a wrapper-local point
  // (x, y) ends up at viewport (panX + W/2 + (x - W/2)*scale, ...).
  // Solving for the zoom-toward-cursor invariant (cursor point stays fixed):
  //   panX' = mx - W/2 - (mx - panX - W/2) * (scale' / scale)
  //
  // ★ HARD GUARD G1 — DO NOT TOUCH ★
  //   Verbatim copy from V1 MidasArtifactDiagramViewer.tsx lines 216-241.
  //   Any change rebreaks hotspot positioning across resizes (per Phase 2
  //   parity checklist Hard Guard G1). The wrapper transform-origin is
  //   `50% 50%` and the wrapper-local math depends on it.
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();

      const rect = viewport.getBoundingClientRect();
      const cx = rect.width / 2;
      const cy = rect.height / 2;
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const oldScale = scaleRef.current;
      const delta = e.deltaY > 0 ? 0.9 : 1.1;
      const newScale = Math.max(0.5, Math.min(oldScale * delta, 15));
      const ratio = newScale / oldScale;
      const newPanX = mx - cx - (mx - panXRef.current - cx) * ratio;
      const newPanY = my - cy - (my - panYRef.current - cy) * ratio;
      setScale(newScale);
      setPanX(newPanX);
      setPanY(newPanY);
    };

    viewport.addEventListener('wheel', handleWheel, { passive: false });
    return () => viewport.removeEventListener('wheel', handleWheel);
  }, []);

  // ── Drag to pan ───────────────────────────────────────────────────
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return;
    setIsDragging(true);
    pannedRef.current = false;
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      panX: panXRef.current,
      panY: panYRef.current,
    };
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!dragStartRef.current) return;
      const dx = e.clientX - dragStartRef.current.x;
      const dy = e.clientY - dragStartRef.current.y;
      if (Math.abs(dx) > PAN_CLICK_SLOP_PX || Math.abs(dy) > PAN_CLICK_SLOP_PX) {
        pannedRef.current = true;
      }
      setPanX(dragStartRef.current.panX + dx);
      setPanY(dragStartRef.current.panY + dy);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  // ── Step navigation ───────────────────────────────────────────────
  const handlePrev = useCallback(() => {
    setActiveStepIdx((idx) => {
      const newIdx = idx > 0 ? idx - 1 : highlightedHotspots.length - 1;
      setSelectedId(highlightedHotspots[newIdx]?.vi_id ?? null);
      return newIdx;
    });
  }, [highlightedHotspots]);

  const handleNext = useCallback(() => {
    setActiveStepIdx((idx) => {
      const newIdx = idx < highlightedHotspots.length - 1 ? idx + 1 : 0;
      setSelectedId(highlightedHotspots[newIdx]?.vi_id ?? null);
      return newIdx;
    });
  }, [highlightedHotspots]);

  // ── Hotspot interaction handlers ──────────────────────────────────
  const handleHotspotMouseEnter = useCallback(
    (id: string, e: React.MouseEvent) => {
      const h = hotspots.find((hs) => hs.vi_id === id);
      pointerRef.current = { x: e.clientX, y: e.clientY }; // PARTS-SERVING-1
      if (h) {
        setTooltipHotspot(h);
        setTooltipPos({ x: e.clientX, y: e.clientY });
      }
    },
    [hotspots]
  );

  const handleHotspotMouseMove = useCallback((e: React.MouseEvent) => {
    pointerRef.current = { x: e.clientX, y: e.clientY }; // PARTS-SERVING-1
    setTooltipPos({ x: e.clientX, y: e.clientY });
  }, []);

  const handleHotspotMouseLeave = useCallback(() => {
    setTooltipHotspot(null);
  }, []);

  // Toggle: click the selected hotspot again → none; click another → it.
  // Box, label pill and leader all land here (same <g> handler).
  // PARTS-SERVING-1: the selection toggle is untouched; a hotspot with parts
  // / details / links also records a pin at the pointer. A toggle-OFF click
  // leaves a pin whose id no longer matches `selectedId` → never shown, and
  // the effect below clears it.
  const handleHotspotClick = useCallback(
    (id: string) => {
      if (pannedRef.current) return; // the trailing click of a pan
      setSelectedId((prev) => (prev === id ? null : id));
      const h = hotspots.find((hs) => hs.vi_id === id);
      setPinned(
        h && hasHotspotBodyExtras(parseHotspotExtras(h))
          ? { id, x: pointerRef.current.x, y: pointerRef.current.y }
          : null
      );
    },
    [hotspots]
  );

  // PARTS-SERVING-1: any selection change away from the pinned hotspot
  // (re-click, backdrop click, step Prev/Next, page change) drops the pin.
  useEffect(() => {
    setPinned((prev) => (prev && prev.id !== selectedId ? null : prev));
  }, [selectedId]);

  const handleBackdropClick = useCallback(() => {
    if (pannedRef.current) return; // the trailing click of a pan
    setSelectedId(null);
  }, []);

  // ── Image load → update contained rect ──
  const handleImageLoad = useCallback(() => {
    updateContainedRect();
  }, [updateContainedRect]);

  // ── ResizeObserver: update contained rect when container size changes ──
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    const observer = new ResizeObserver(() => {
      updateContainedRect();
    });

    observer.observe(viewport);
    return () => { observer.disconnect(); };
  }, [updateContainedRect]);

  // ── Reset view → back to scale=1, no pan ──
  const handleResetView = useCallback(() => {
    setScale(1);
    setPanX(0);
    setPanY(0);
  }, []);

  const activeHighlightedId = highlightedHotspots[activeStepIdx]?.vi_id;
  const scalePercent = Math.round(scale * 100);
  // PARTS-SERVING-1: the pinned card's hotspot, or null. Hidden with the
  // overlays (the eye toggle keeps the selection, so it reappears with them).
  const pinnedHotspot =
    pinned && pinned.id === selectedId && showOverlays
      ? hotspots.find((h) => h.vi_id === pinned.id) ?? null
      : null;
  // OVERLAY-LABEL-PLACEMENT-1: rendered image W/H (only read by anchored pills).
  const imageAspect = resolveImageAspect(containedRect.width, containedRect.height);

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* Viewport area — bone background with subtle dot grid for orientation */}
      <div
        ref={viewportRef}
        onMouseDown={handleMouseDown}
        onClick={handleBackdropClick}
        className={[
          "relative flex-1 select-none overflow-hidden bg-midas-bone",
          isDragging ? "cursor-grabbing" : "cursor-grab",
        ].join(" ")}
        style={{
          // Dot grid recipe — verbatim from V1
          backgroundImage:
            "radial-gradient(circle at 1px 1px, rgba(0,0,0,0.06) 1px, transparent 0)",
          backgroundSize: "16px 16px",
        }}
      >
        {loading && (
          <div
            className="m-4 h-[300px] w-[calc(100%-2rem)] animate-pulse rounded-md bg-midas-warm-stone/60"
            aria-hidden="true"
          />
        )}

        {error && !loading && (
          <div className="flex h-full w-full items-center justify-center font-sora text-[13px] text-midas-muted-stone">
            {translate("midas.artifact.loadError")}
          </div>
        )}

        {blobUrl && !loading && !error && (
          <>
            {/* Single rendering mode: transform wrapper around CSS-fit image */}
            <div
              style={{
                width: "100%",
                height: "100%",
                transform: `translate(${panX}px, ${panY}px) scale(${scale})`,
                transformOrigin: "50% 50%",
              }}
            >
              <img
                ref={imgRef}
                src={blobUrl}
                alt={tab.artifact.title}
                draggable={false}
                onLoad={handleImageLoad}
                style={{
                  display: "block",
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  userSelect: "none",
                  // OVERLAY-LABEL-PLACEMENT-1: reserve the left label gutter
                  // inside the fit-to-view box (matches getContainedRect).
                  ...(labelGutterPct > 0
                    ? {
                        boxSizing: "border-box" as const,
                        paddingLeft: (containedRect.width * labelGutterPct) / 100,
                      }
                    : {}),
                }}
              />
              {/* SVG overlay positioned to match where object-fit:contain places the image */}
              {showOverlays && (
                <svg
                  style={{
                    position: "absolute",
                    left: containedRect.left,
                    top: containedRect.top,
                    width: containedRect.width,
                    height: containedRect.height,
                    pointerEvents: "none",
                    // OVERLAY-LABEL-PLACEMENT-1: anchored pills may sit left
                    // of the image (negative x_pct, in the gutter) — let them
                    // paint outside the svg box. Pages without anchors keep
                    // the default clipping.
                    ...(hasLabelAnchors ? { overflow: "visible" as const } : {}),
                  }}
                  viewBox="0 0 100 100"
                  preserveAspectRatio="none"
                >
                  <g pointerEvents="all">
                    {hotspots.map((h, i) => {
                      // OVERLAY-LABEL-PLACEMENT-1: anchor props only on
                      // anchored hotspots; legacy-rule hotspots get exactly
                      // the props they had before (→ original label rule).
                      // HIDDEN-ONLY-1: `labelHidden` only on rule "none".
                      const anchor = labelAnchors?.[i] || undefined;
                      const labelHidden =
                        labelPlans?.[i]?.rule === "none" ? true : undefined;
                      return (
                        <DiagramHotspotRectV2
                          key={h.vi_id}
                          hotspot={h}
                          isHighlighted={isHotspotHighlighted(h)}
                          isActive={h.vi_id === activeHighlightedId}
                          hasAnyHighlighted={hasAnyHighlighted}
                          // DIAG-OVERLAY-B: undefined on every hotspot that
                          // isn't part of the flashed component (and always,
                          // when nothing is flashing) → zero visual change.
                          flashMode={isHotspotFlashing(h) ? flashMode : undefined}
                          labelAnchor={anchor}
                          labelPill={anchor ? labelPill || undefined : undefined}
                          imageAspect={anchor ? imageAspect : undefined}
                          labelHidden={labelHidden}
                          // OVERLAY-CLICK-HIGHLIGHT-1: drawn in array order
                          // like every other hotspot (no raise, no sort).
                          isSelected={h.vi_id === selectedId}
                          onMouseEnter={handleHotspotMouseEnter}
                          onMouseMove={handleHotspotMouseMove}
                          onMouseLeave={handleHotspotMouseLeave}
                          onClick={handleHotspotClick}
                        />
                      );
                    })}
                  </g>
                </svg>
              )}
            </div>

            {/* Floating tooltip (follows cursor, rendered outside transform).
                PARTS-SERVING-1: not for the hotspot whose card is pinned. */}
            {tooltipHotspot && tooltipHotspot.vi_id !== pinnedHotspot?.vi_id && (
              <DiagramInfoCardV2
                hotspot={tooltipHotspot}
                mouseX={tooltipPos.x}
                mouseY={tooltipPos.y}
              />
            )}
          </>
        )}
      </div>

      {/* PARTS-SERVING-1: pinned card — a SIBLING of the viewport, so its
          clicks / drags / wheel never reach the pan, zoom or backdrop
          handlers above. */}
      {pinned && pinnedHotspot && blobUrl && !loading && !error && (
        <DiagramPinnedInfoCardV2
          hotspot={pinnedHotspot}
          x={pinned.x}
          y={pinned.y}
          viewportEl={viewportRef.current}
          onOpenLink={onOpenSheet}
        />
      )}

      {/* Bottom toolbar: step nav · scale display · overlay toggle · reset */}
      <div className="flex h-[40px] flex-shrink-0 items-center justify-between border-t border-midas-stone bg-midas-bone-light px-2">
        {/* Step navigation (only if multiple highlighted components) */}
        <div className="flex min-w-[120px] items-center gap-1">
          {highlightedHotspots.length > 1 && (
            <>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={handlePrev}
                    aria-label={translate("midas.artifact.diagram.prev")}
                    className="inline-flex h-7 w-7 items-center justify-center rounded-md text-midas-warm-graphite transition-colors hover:bg-midas-stone/40 hover:text-midas-graphite"
                  >
                    <ChevronLeft size={16} strokeWidth={2} />
                  </button>
                </TooltipTrigger>
                <TooltipContent
                  side="top"
                  className="border-midas-stone bg-midas-graphite font-sora text-[11px] text-midas-bone"
                >
                  {translate("midas.artifact.diagram.prev")}
                </TooltipContent>
              </Tooltip>

              <span
                className="font-mono text-[11px] font-semibold text-midas-warm-graphite text-center"
                style={{ minWidth: "30px" }}
              >
                {activeStepIdx + 1}/{highlightedHotspots.length}
              </span>

              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={handleNext}
                    aria-label={translate("midas.artifact.diagram.next")}
                    className="inline-flex h-7 w-7 items-center justify-center rounded-md text-midas-warm-graphite transition-colors hover:bg-midas-stone/40 hover:text-midas-graphite"
                  >
                    <ChevronRight size={16} strokeWidth={2} />
                  </button>
                </TooltipTrigger>
                <TooltipContent
                  side="top"
                  className="border-midas-stone bg-midas-graphite font-sora text-[11px] text-midas-bone"
                >
                  {translate("midas.artifact.diagram.next")}
                </TooltipContent>
              </Tooltip>
            </>
          )}
        </div>

        {/* Scale display + Overlay toggle + Reset view */}
        <div className="flex items-center gap-[6px]">
          <span
            className="font-mono text-[11px] font-semibold text-midas-warm-graphite text-center"
            style={{ minWidth: "36px" }}
            aria-live="polite"
          >
            {scalePercent}%
          </span>

          {/* Hairline separator */}
          <span aria-hidden="true" className="mx-1 inline-block h-4 w-px bg-midas-stone" />

          {/* R5 sub-task 2a (2026-05-19): hide internal eye toggle when wrapped
              by v3. v3 owns the eye toggle in the dock header (ArtifactHeaderV3
              R3 sub-task 1). undefined/false keeps v2 caller byte-identical. */}
          {!hideInternalEyeToggle && (
            <>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => setShowOverlays((prev) => !prev)}
                    aria-label={
                      showOverlays
                        ? translate("midas.artifact.diagram.hideOverlays")
                        : translate("midas.artifact.diagram.showOverlays")
                    }
                    className={[
                      "inline-flex h-7 w-7 items-center justify-center rounded-md transition-colors hover:bg-midas-stone/40 hover:text-midas-graphite",
                      showOverlays ? "text-midas-warm-graphite" : "text-midas-muted-stone",
                    ].join(" ")}
                  >
                    {showOverlays ? <Eye size={16} strokeWidth={2} /> : <EyeOff size={16} strokeWidth={2} />}
                  </button>
                </TooltipTrigger>
                <TooltipContent
                  side="top"
                  className="border-midas-stone bg-midas-graphite font-sora text-[11px] text-midas-bone"
                >
                  {showOverlays
                    ? translate("midas.artifact.diagram.hideOverlays")
                    : translate("midas.artifact.diagram.showOverlays")}
                </TooltipContent>
              </Tooltip>

              <span aria-hidden="true" className="mx-1 inline-block h-4 w-px bg-midas-stone" />
            </>
          )}

          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={handleResetView}
                aria-label={translate("midas.artifact.diagram.resetView")}
                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-midas-warm-graphite transition-colors hover:bg-midas-stone/40 hover:text-midas-graphite"
              >
                <Maximize2 size={16} strokeWidth={2} />
              </button>
            </TooltipTrigger>
            <TooltipContent
              side="top"
              className="border-midas-stone bg-midas-graphite font-sora text-[11px] text-midas-bone"
            >
              {translate("midas.artifact.diagram.resetView")}
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

export default MidasArtifactDiagramViewerV2;
